<template>
  <div
    v-if="showModal"
    class="modal fade"
    :class="{
      in: showModal
    }"
    id="new-customer"
    :style="{
      display: showModal ? 'block' : 'none',
      background: showModal ? '#0005' : 'transparent'
    }"
  >
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <button
            type="button"
            class="close"
            :class="{ 'is-disabled': updating }"
            aria-hidden="true"
            @click="() => closeModal()"
          >
            &times;
          </button>
          <h4 class="modal-title" id="searchModalLabel">
            <i class="fa fa-fw fa-lg fa-user-plus text-primary"></i>
            {{ $label("booking.new_customer", "New customer") }}
          </h4>
        </div>
        <div class="modal-body v-form">
          <fieldset>
            <alert-box
              class="mt-4"
              v-if="errorMessage !== null"
              type="danger"
              id="alert-create-customer-server-error"
              :closeable="false"
              :show-icon="false"
            >
              {{ errorMessage }}
            </alert-box>
            <div class="row">
              <section class="col col-6">
                <label class="label">{{
                  $label("common.gender", "Gender")
                }}</label>

                <label
                  class="btn btn-lg btn-default v-form-button"
                  id="customer-gender-female"
                  :class="{ 'checked-gender': newCustomer.gender === 1 }"
                  @change="toggleGender(1)"
                >
                  <i class="fa fa-fw fa-lg fa-female" />
                  <input type="checkbox" class="display-none" />
                </label>
                <label
                  class="btn btn-lg btn-default v-form-button"
                  id="customer-gender-male"
                  :class="{ 'checked-gender': newCustomer.gender === 2 }"
                  @change="toggleGender(2)"
                >
                  <i class="fa fa-fw fa-lg fa-male" />
                  <input type="checkbox" class="display-none" />
                </label>

                <p v-if="hasError('gender')" class="text-danger">
                  {{ getError("gender") }}
                </p>
              </section>
              <section class="col col-6">
                <label class="label">{{
                  $label("common.title", "Title")
                }}</label>
                <label class="input">
                  <input
                    v-model="newCustomer.title"
                    :class="{ 'text-input-error': hasError('title') }"
                    :disabled="updating"
                    :placeholder="$label('common.title', 'Title')"
                    id="customer-title"
                  />
                </label>
                <p v-if="hasError('title')" class="text-danger">
                  {{ getError("title") }}
                </p>
              </section>
            </div>
            <div class="row">
              <section class="col col-6">
                <label class="label">{{
                  $label("common.last_name", "Last name")
                }}</label>
                <label class="input">
                  <input
                    v-model="newCustomer.last_name"
                    :class="{ 'text-input-error': hasError('last_name') }"
                    :disabled="updating"
                    :placeholder="$label('customer.last_name', 'Last name')"
                    id="customer-last-name"
                  />
                </label>
                <p v-if="hasError('last_name')" class="text-danger">
                  {{ getError("last_name") }}
                </p>
              </section>
              <section class="col col-6">
                <label class="label">{{
                  $label("common.first_name", "First name")
                }}</label>
                <label class="input">
                  <input
                    v-model="newCustomer.first_name"
                    :class="{ 'text-input-error': hasError('first_name') }"
                    :disabled="updating"
                    :placeholder="$label('customer.first_name', 'First name')"
                    id="customer-first-name"
                  />
                </label>
                <p v-if="hasError('first_name')" class="text-danger">
                  {{ getError("first_name") }}
                </p>
              </section>
            </div>
            <div class="row mb-8"><div class="col-xs-12">&nbsp;</div></div>
            <div class="row">
              <section class="col col-12">
                <label class="label">{{
                  $label("common.email_address_long", "E-Mail address")
                }}</label>
                <label class="input">
                  <i class="icon-append icon-envelope"></i>
                  <input
                    v-model="newCustomer.email"
                    :class="{ 'text-input-error': hasError('email') }"
                    :disabled="updating"
                    :placeholder="$label('common.email_address_short', 'Email')"
                    id="customer-email"
                  />
                </label>
                <p v-if="hasError('email')" class="text-danger">
                  {{ getError("email") }}
                </p>
              </section>
            </div>
            <div class="row">
              <section class="col col-3">
                <label class="label">{{
                  $label("common.prefix", "Prefix")
                }}</label>
                <label class="input">
                  <input
                    v-model="newCustomer.phone_number.prefix"
                    :class="{
                      'text-input-error': hasError('phone_number.prefix')
                    }"
                    :disabled="updating"
                    placeholder="XX"
                    id="customer-mobile-phone-prefix"
                  />
                </label>
                <p v-if="hasError('phone_number.prefix')" class="text-danger">
                  {{ getError("phone_number.prefix") }}
                </p>
              </section>
              <section class="col col-9">
                <label class="label">{{
                  $label("common.mobile_phone_number", "Mobile phone number")
                }}</label>
                <label class="input">
                  <i class="icon-append fa fa-mobile-phone"></i>
                  <input
                    v-model="newCustomer.phone_number.number"
                    :class="{
                      'text-input-error': hasError('phone_number.number')
                    }"
                    :disabled="updating"
                    :placeholder="
                      $label('common.phone_number_placeholder', 'XXXXXXXX')
                    "
                    id="customer-mobile-phone-number"
                  />
                </label>
                <p v-if="hasError('phone_number.number')" class="text-danger">
                  {{ getError("phone_number.number") }}
                </p>
              </section>
            </div>
            <div class="row mb-8"><div class="col-xs-12">&nbsp;</div></div>
            <div class="row">
              <section class="col col-6">
                <label class="label">{{
                  $label("common.position", "Position")
                }}</label>
                <label class="input">
                  <input
                    v-model="newCustomer.position"
                    :class="{ 'text-input-error': hasError('position') }"
                    :disabled="updating"
                    :placeholder="$label('common.position', 'Position | Job')"
                    id="customer-position"
                  />
                </label>
                <p v-if="hasError('position')" class="text-danger">
                  {{ getError("position") }}
                </p>
              </section>
              <section class="col col-6">
                <label class="label">{{
                  $label("common.date_of_birth", "Date of birth")
                }}</label>
                <date-picker
                  v-model="newCustomer.birthdate"
                  :format="dateFormat"
                  :readonly="updating"
                  :disabled="updating"
                  class="input"
                  id="customer-birthdate"
                  :class="{ 'text-input-error': hasError('birthdate') }"
                  placeholder="DD.MM.YYYY"
                >
                </date-picker>
                <p v-if="hasError('birthdate')" class="text-danger">
                  {{ getError("birthdate") }}
                </p>
              </section>
            </div>
            <div class="row">
              <section class="col col-12">
                <label class="label">{{
                  $label("customer.note", "Internal note")
                }}</label>
                <label class="textarea">
                  <textarea
                    v-model="newCustomer.note"
                    :class="{ 'text-input-error': hasError('note') }"
                    :placeholder="$label('customer.note', 'Note')"
                    :disabled="updating"
                    id="customer-note"
                    rows="3"
                  >
                  </textarea>
                </label>
              </section>
            </div>
            <div class="row mb-8"><div class="col-xs-12">&nbsp;</div></div>
          </fieldset>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            :class="{ 'is-disabled': updating }"
            id="reset-filter"
            class="btn btn-default"
            @click="() => closeModal()"
          >
            {{ $label("common.abort", "Abort") }}
          </button>
          <button
            type="button"
            :class="{ 'is-disabled': updating }"
            id="submit-filter"
            class="btn btn-primary"
            @click="createCustomer"
          >
            {{ $label("customer.create", "Create customer") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Api from "@/api";
import swal from "sweetalert";
import FormErrorsMixin from "@/mixins/FormErrors";

import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import "vue2-datepicker/locale/de";

import AlertBox from "@/components/Common/Alerts/AlertBox";

export default {
  name: "NewCustomerModal",

  mixins: [FormErrorsMixin],

  components: {
    AlertBox,
    DatePicker
  },

  props: {
    showModal: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      updating: false,
      dateFormat: "DD.MM.YYYY",
      failedRequest: false,
      errorMessage: null,
      newCustomer: {
        first_name: null,
        last_name: null,
        gender: null,
        title: null,
        email: null,
        birthdate: null,
        color_code: null,
        note: null,
        position: null,
        number: null,
        phone_number: {
          prefix: null,
          number: null
        }
      },
      validatedInputs: [
        "first_name",
        "last_name",
        "gender",
        "title",
        "email",
        "birthdate",
        "color_code",
        "note",
        "position",
        "number",
        "phone_number.prefix",
        "phone_number.number"
      ]
    };
  },
  methods: {
    parseBirthdate(val) {
      if (
        this.newCustomer.birthdate != null &&
        Object.prototype.toString.call(this.newCustomer.birthdate) ===
          "[object Date]"
      ) {
        let val = this.newCustomer.birthdate;
        this.newCustomer.birthdate =
          val.getFullYear() +
          "-" +
          (val.getMonth() + 1 > 9 ? "" : "0") +
          (val.getMonth() + 1) +
          "-" +
          (val.getDate() > 9 ? "" : "0") +
          val.getDate();
      } else if (this.newCustomer.birthdate != null && val != null) {
        let parts = this.newCustomer.birthdate.split("-");
        this.newCustomer.birthdate = new Date(parts[0], parts[1] - 1, parts[2]);
      } else if (this.newCustomer.birthdate != null && val == null) {
        let parts = this.newCustomer.birthdate.split(".");
        this.newCustomer.birthdate = parts[2] + "-" + parts[1] + "-" + parts[0];
      }
    },

    createCustomer() {
      if (this.updating) {
        return;
      }

      this.resetErrors();
      this.updating = true;
      const endpoint = this.$partnerEndpoint("customers");

      this.parseBirthdate();

      Api()
        .post(endpoint, this.newCustomer)
        .then(response => {
          this.failedRequest = false;
          swal("Success!", "The customer was created.", "success");
          this.closeModal(response.data.data);
        })
        .catch(error => {
          this.failedRequest = true;
          this.storeErrors(error.response.data.errors);

          if (parseInt(error.response.status / 100, 10) === 5) {
            this.errorMessage = "An error occurred.";
          }
        })
        .finally(() => {
          this.updating = false;
        });
    },

    closeModal(newCustomer) {
      if (newCustomer) {
        this.$emit("onSuccess", newCustomer);
      } else {
        this.$emit("close");
      }
      this.newCustomer = {
        first_name: null,
        last_name: null,
        gender: null,
        title: null,
        email: null,
        birthdate: null,
        color_code: null,
        note: null,
        position: null,
        number: null,
        phone_number: {
          prefix: null,
          number: null
        }
      };
    },

    toggleGender(gender) {
      if (gender === this.newCustomer.gender) {
        if (gender === 2) {
          this.newCustomer.gender = 1;
        } else {
          this.newCustomer.gender = 2;
        }
      } else {
        this.newCustomer.gender = gender;
      }
    }
  }
};
</script>

<style scoped>
.checked-gender {
  color: #333;
  background-color: #e6e6e6;
  border-color: #adadad;
}
</style>
